import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import useFixedRowActionsMenu, { ROW_ACTIONS_MENU_HEIGHT } from '../../../Hooks/useFixedRowActionsMenu';
import AdminLayout from '../../../components/AdminLayout/AdminLayout';
import DashboardMainContainer from '../../../components/common/Containers/DashboardMainContainer';
import {
  AdminPageTitle,
  AdminSupportingText,
  AdminSearchField,
  AdminListFilterDropdown,
  AdminSortableTableHeader,
  AdminTableHeader,
  AdminTableBody,
  AdminTableMeta,
  AdminStatusBadge,
  AdminRowAvatar,
  AdminRowActionsButton,
  AdminRowMenuItem,
  AdminFixedRowActionsMenu,
  AdminRowAction,
  AdminAlertText,
  AdminRowMenuIcon,
  ADMIN_TOOLBAR_CLASS,
  ADMIN_TABLE_CLASS,
} from '../shared/adminUx';
import ConfigurationListPaginationFooter from '../../../components/AdminDashboard/Configuration/ConfigurationListPaginationFooter';
import useClickOutside from '../../../Hooks/useClickOutside';
import defaultUserAvatar from '../../../assets/img/User.png';
import { CONFIG_LIST_PAGE_SIZE } from '../../../utils/configurationTableUx';
import {
  APPEAL_STATUS_OPTIONS,
  APPEAL_SOURCE_OPTIONS,
  APPEAL_CASE_TYPE_OPTIONS,
  buildSampleAppealCases,
  displayHandlerName,
  getAppealCaseType,
} from '../../../data/appealsCasesData';
import {
  ADMIN_OPENED_CASE_KEYS,
  applyOpenedNewToInProgress,
  openCaseAndAdvanceStatus,
} from '../../../utils/adminOpenedCases';

/**
 * Screen 1 columns (11):
 * Appeal ID, Decision ID, Account, Source, Type,
 * Decision, Support Team, Status, Submitted, Updated, Actions
 */
const APPEALS_GRID =
  'minmax(86px, 0.8fr) minmax(100px, 0.95fr) minmax(120px, 1.15fr) minmax(100px, 0.95fr) minmax(100px, 0.95fr) minmax(108px, 1.1fr) minmax(92px, 0.9fr) minmax(100px, 0.95fr) minmax(118px, 1fr) minmax(118px, 1fr) minmax(44px, 48px)';

const STATUS_FILTER_OPTIONS = [
  { value: null, label: 'All' },
  ...APPEAL_STATUS_OPTIONS.map((status) => ({ value: status, label: status })),
];

const SOURCE_FILTER_OPTIONS = [
  { value: null, label: 'All' },
  ...APPEAL_SOURCE_OPTIONS.map((source) => ({ value: source, label: source })),
];

const TYPE_FILTER_OPTIONS = [
  { value: null, label: 'All' },
  ...APPEAL_CASE_TYPE_OPTIONS.map((type) => ({ value: type, label: type })),
];

const Appeals = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const gridTemplateColumns = APPEALS_GRID;

  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [filters, setFilters] = useState({
    status: null,
    source: null,
    caseType: null,
  });

  const [statusOpen, setStatusOpen] = useState(false);
  const [sourceOpen, setSourceOpen] = useState(false);
  const [caseTypeOpen, setCaseTypeOpen] = useState(false);
  const statusRef = useRef(null);
  const sourceRef = useRef(null);
  const caseTypeRef = useRef(null);

  const [rowActionsOpen, setRowActionsOpen] = useState({});
  const rowActionsRefs = useRef({});
  const tableScrollRef = useRef(null);
  const {
    layout: rowActionsMenuLayout,
    menuRef: rowActionsMenuRef,
    placementReady: rowActionsMenuPlacementReady,
  } = useFixedRowActionsMenu(
    rowActionsOpen,
    rowActionsRefs,
    tableScrollRef,
    ROW_ACTIONS_MENU_HEIGHT.SINGLE
  );

  const [allAppealItems, setAllAppealItems] = useState(() =>
    buildSampleAppealCases().map((appeal) =>
      applyOpenedNewToInProgress(ADMIN_OPENED_CASE_KEYS.appeals, appeal, 'appealId')
    )
  );

  const [appeals, setAppeals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalFilteredCount, setTotalFilteredCount] = useState(0);
  const [sortField, setSortField] = useState('lastUpdated');
  const [sortOrder, setSortOrder] = useState('desc');

  const toggleRowActions = (rowKey) => {
    setRowActionsOpen((prev) => {
      if (prev[rowKey]) return {};
      return { [rowKey]: true };
    });
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      const isOutsideAll = Object.values(rowActionsRefs.current).every(
        (ref) => ref && !ref.contains(event.target)
      );
      if (isOutsideAll) setRowActionsOpen({});
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useClickOutside(statusRef, () => setStatusOpen(false));
  useClickOutside(sourceRef, () => setSourceOpen(false));
  useClickOutside(caseTypeRef, () => setCaseTypeOpen(false));

  const matchesSearchQuery = (appeal, searchTerm) => {
    if (!searchTerm || searchTerm.trim().length === 0) return true;
    const term = searchTerm.toLowerCase().trim();
    return (
      appeal.appealId?.toLowerCase().includes(term) ||
      appeal.originalDecisionId?.toLowerCase().includes(term) ||
      appeal.submittedByName?.toLowerCase().includes(term) ||
      appeal.accountId?.toLowerCase().includes(term)
    );
  };

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(searchQuery), 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      const filterRefs = [statusRef, sourceRef, caseTypeRef];
      const isOutsideAll = filterRefs.every((ref) => {
        const el = ref?.current;
        return !el || !el.contains(event.target);
      });
      if (isOutsideAll) {
        setStatusOpen(false);
        setSourceOpen(false);
        setCaseTypeOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('touchstart', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, []);

  useEffect(() => {
    const fetchAppeals = async () => {
      setLoading(true);
      setError(null);
      try {
        await new Promise((resolve) => setTimeout(resolve, 400));

        let filtered = allAppealItems.filter((appeal) => {
          const matchesSearch = matchesSearchQuery(appeal, debouncedSearch);
          const matchesStatus = filters.status === null || appeal.status === filters.status;
          const matchesSource = filters.source === null || appeal.source === filters.source;
          const matchesCaseType =
            filters.caseType === null || getAppealCaseType(appeal) === filters.caseType;
          return matchesSearch && matchesStatus && matchesSource && matchesCaseType;
        });

        filtered.sort((a, b) => {
          let aValue = a[sortField];
          let bValue = b[sortField];

          if (sortField === 'submittedOn' || sortField === 'lastUpdated') {
            aValue = new Date(a[sortField]).getTime();
            bValue = new Date(b[sortField]).getTime();
          }

          if (sortField === 'assignedHandler') {
            aValue = displayHandlerName(a.assignedHandler);
            bValue = displayHandlerName(b.assignedHandler);
          } else if (sortField === 'caseType') {
            aValue = getAppealCaseType(a);
            bValue = getAppealCaseType(b);
          }

          let cmp = 0;
          if (aValue === null || aValue === undefined) cmp = 1;
          else if (bValue === null || bValue === undefined) cmp = -1;
          else if (typeof aValue === 'string' && typeof bValue === 'string') {
            cmp = aValue.localeCompare(bValue);
          } else {
            cmp = aValue < bValue ? -1 : aValue > bValue ? 1 : 0;
          }

          if (sortOrder === 'desc') cmp = -cmp;

          // Default tie-break: Appeal ID ascending when Updated times match
          if (cmp === 0) {
            return String(a.appealId || '').localeCompare(String(b.appealId || ''));
          }
          return cmp;
        });

        const totalCount = filtered.length;
        const computedTotalPages = Math.max(1, Math.ceil(totalCount / CONFIG_LIST_PAGE_SIZE));
        const clampedPage = Math.min(currentPage, computedTotalPages);
        const startIndex = (clampedPage - 1) * CONFIG_LIST_PAGE_SIZE;

        setTotalFilteredCount(totalCount);
        setTotalPages(computedTotalPages);
        setAppeals(filtered.slice(startIndex, startIndex + CONFIG_LIST_PAGE_SIZE));

        if (clampedPage !== currentPage) {
          setCurrentPage(clampedPage);
        }
      } catch (err) {
        setError(err.message || 'Failed to load appeals');
      } finally {
        setLoading(false);
      }
    };
    fetchAppeals();
  }, [allAppealItems, debouncedSearch, filters, currentPage, sortField, sortOrder]);

  const handleFilterChange = (filterName, value) => {
    setFilters((prev) => ({ ...prev, [filterName]: value }));
    setCurrentPage(1);
  };

  const handleSort = (field) => {
    if (sortField === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortOrder(field === 'appealId' ? 'asc' : 'desc');
    }
  };

  const formatDateTime = (date) => {
    if (!date) return '—';
    const d = date instanceof Date ? date : new Date(date);
    const datePart = d.toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' });
    const timePart = d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
    return `${datePart}, ${timePart}`;
  };

  const openAppeal = (appeal) => {
    const openedAppeal = openCaseAndAdvanceStatus(
      ADMIN_OPENED_CASE_KEYS.appeals,
      appeal,
      'appealId'
    );

    if (openedAppeal.status !== appeal.status) {
      setAllAppealItems((prev) =>
        prev.map((row) => (row.appealId === appeal.appealId ? openedAppeal : row))
      );
    }

    navigate(`/admin/appeals/${appeal.appealId}`, {
      state: {
        from: location.pathname,
        fromSearch: location.search,
        appeal: openedAppeal,
      },
    });
  };

  const effectivePage = Math.min(currentPage, totalPages);
  const rangeStart =
    totalFilteredCount === 0 ? 0 : (effectivePage - 1) * CONFIG_LIST_PAGE_SIZE + 1;
  const rangeEnd = Math.min(effectivePage * CONFIG_LIST_PAGE_SIZE, totalFilteredCount);
  const showPagination = totalFilteredCount > CONFIG_LIST_PAGE_SIZE;

  return (
    <AdminLayout>
      <div>
        <AdminPageTitle>Appeals</AdminPageTitle>

        <div className={ADMIN_TOOLBAR_CLASS}>
          <div className="flex flex-col lg:flex-row gap-4 items-start lg:items-center lg:justify-between">
            <AdminSearchField
              className="w-full max-w-[26.25rem]"
              type="text"
              placeholder="Search Appeal ID, Decision ID, Account, or Account ID."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              aria-label="Search appeals"
            />

            <div className="flex flex-wrap gap-3 items-center w-full lg:w-auto lg:justify-end">
              <AdminListFilterDropdown
                label="Source"
                value={filters.source}
                options={SOURCE_FILTER_OPTIONS}
                onChange={(value) => handleFilterChange('source', value)}
                menuRef={sourceRef}
                open={sourceOpen}
                setOpen={setSourceOpen}
              />
              <AdminListFilterDropdown
                label="Type"
                value={filters.caseType}
                options={TYPE_FILTER_OPTIONS}
                onChange={(value) => handleFilterChange('caseType', value)}
                menuRef={caseTypeRef}
                open={caseTypeOpen}
                setOpen={setCaseTypeOpen}
              />
              <AdminListFilterDropdown
                label="Status"
                value={filters.status}
                options={STATUS_FILTER_OPTIONS}
                onChange={(value) => handleFilterChange('status', value)}
                menuRef={statusRef}
                open={statusOpen}
                setOpen={setStatusOpen}
              />
            </div>
          </div>
        </div>

        <DashboardMainContainer className={ADMIN_TABLE_CLASS}>
          {loading ? (
            <div className="space-y-4">
              {[...Array(5)].map((_, i) => (
                <div key={i} className="h-12 bg-white animate-pulse rounded" />
              ))}
            </div>
          ) : error ? (
            <div className="text-center py-10">
              <AdminAlertText className="mb-4">{error}</AdminAlertText>
              <AdminRowAction size="sm" onClick={() => window.location.reload()}>
                Retry
              </AdminRowAction>
            </div>
          ) : appeals.length === 0 ? (
            <div className="text-center py-10">
              <AdminSupportingText className="text-mediumgray">
                No appeals match your filters.
              </AdminSupportingText>
            </div>
          ) : (
            <div ref={tableScrollRef} className="overflow-x-auto">
              <div className="min-w-[1180px]">
                <div
                  className="grid py-3 text-offblack border-b border-lightgray px-4 gap-2 lg:gap-3"
                  style={{ gridTemplateColumns }}
                >
                  <div className="flex items-center min-w-0">
                    <AdminSortableTableHeader className="text-left" onClick={() => handleSort('appealId')}>
                      Appeal ID
                    </AdminSortableTableHeader>
                  </div>
                  <div className="flex items-center min-w-0">
                    <AdminSortableTableHeader
                      className="text-left"
                      onClick={() => handleSort('originalDecisionId')}
                    >
                      Decision ID
                    </AdminSortableTableHeader>
                  </div>
                  <div className="flex items-center gap-2 min-w-0">
                    <span className="w-6 h-6 shrink-0" aria-hidden />
                    <AdminTableHeader className="text-left">Account</AdminTableHeader>
                  </div>
                  <div className="flex items-center min-w-0">
                    <AdminSortableTableHeader className="text-left" onClick={() => handleSort('source')}>
                      Source
                    </AdminSortableTableHeader>
                  </div>
                  <div className="flex items-center min-w-0">
                    <AdminSortableTableHeader className="text-left" onClick={() => handleSort('caseType')}>
                      Type
                    </AdminSortableTableHeader>
                  </div>
                  <div className="flex items-center min-w-0">
                    <AdminSortableTableHeader
                      className="text-left"
                      onClick={() => handleSort('decisionType')}
                    >
                      Decision
                    </AdminSortableTableHeader>
                  </div>
                  <div className="flex items-center min-w-0">
                    <AdminSortableTableHeader
                      className="text-left"
                      onClick={() => handleSort('assignedHandler')}
                    >
                      Support Team
                    </AdminSortableTableHeader>
                  </div>
                  <div className="flex items-center min-w-0">
                    <AdminSortableTableHeader className="text-left" onClick={() => handleSort('status')}>
                      Status
                    </AdminSortableTableHeader>
                  </div>
                  <div className="flex items-center min-w-0">
                    <AdminSortableTableHeader
                      className="text-left"
                      onClick={() => handleSort('submittedOn')}
                    >
                      Submitted
                    </AdminSortableTableHeader>
                  </div>
                  <div className="flex items-center min-w-0">
                    <AdminSortableTableHeader
                      className="text-left"
                      onClick={() => handleSort('lastUpdated')}
                    >
                      Updated
                    </AdminSortableTableHeader>
                  </div>
                  <div className="flex items-center justify-center">
                    <AdminTableHeader>Actions</AdminTableHeader>
                  </div>
                </div>

                {appeals.map((appeal, index) => {
                  const rowKey = appeal.appealId || `idx-${index}`;
                  const handlerLabel =
                    displayHandlerName(appeal.assignedHandler) === 'Unassigned'
                      ? '—'
                      : displayHandlerName(appeal.assignedHandler);
                  const caseTypeLabel = getAppealCaseType(appeal);
                  return (
                    <div
                      key={appeal.appealId || index}
                      className="grid py-3 px-4 text-offblack border-b border-lightgray last:border-b-0 gap-2 lg:gap-3 cursor-pointer"
                      style={{ gridTemplateColumns }}
                      onClick={() => openAppeal(appeal)}
                    >
                      <div className="flex items-center min-w-0">
                        <AdminTableBody className="text-left w-full truncate" title={appeal.appealId}>
                          {appeal.appealId || '—'}
                        </AdminTableBody>
                      </div>

                      <div className="flex items-center min-w-0">
                        <AdminTableBody
                          className="text-left w-full truncate"
                          title={appeal.originalDecisionId}
                        >
                          {appeal.originalDecisionId || '—'}
                        </AdminTableBody>
                      </div>

                      <div className="flex items-center gap-2 min-w-0">
                        <AdminRowAvatar
                          src={appeal.accountAvatar || defaultUserAvatar}
                          alt={appeal.submittedByName || '—'}
                          onError={(e) => {
                            e.target.src = defaultUserAvatar;
                          }}
                        />
                        <AdminTableBody
                          className="truncate text-left w-full"
                          title={appeal.submittedByName || '—'}
                        >
                          {appeal.submittedByName || '—'}
                        </AdminTableBody>
                      </div>

                      <div className="flex items-center min-w-0">
                        <AdminTableBody className="text-left w-full truncate" title={appeal.source}>
                          {appeal.source || '—'}
                        </AdminTableBody>
                      </div>

                      <div className="flex items-center min-w-0">
                        <AdminTableBody className="text-left w-full truncate" title={caseTypeLabel}>
                          {caseTypeLabel}
                        </AdminTableBody>
                      </div>

                      <div className="flex items-center min-w-0">
                        <AdminTableBody
                          className="text-left w-full truncate"
                          title={appeal.decisionType}
                        >
                          {appeal.decisionType || '—'}
                        </AdminTableBody>
                      </div>

                      <div className="flex items-center min-w-0">
                        <AdminTableBody className="text-left w-full truncate" title={handlerLabel}>
                          {handlerLabel}
                        </AdminTableBody>
                      </div>

                      <div className="flex items-center min-w-0">
                        <AdminStatusBadge
                          status={appeal.status}
                          label={appeal.status}
                          className="whitespace-nowrap"
                        />
                      </div>

                      <div className="flex items-center min-w-0">
                        <AdminTableMeta
                          className="text-left whitespace-nowrap"
                          title={formatDateTime(appeal.submittedOn)}
                        >
                          {formatDateTime(appeal.submittedOn)}
                        </AdminTableMeta>
                      </div>

                      <div className="flex items-center min-w-0">
                        <AdminTableMeta
                          className="text-left whitespace-nowrap"
                          title={formatDateTime(appeal.lastUpdated)}
                        >
                          {formatDateTime(appeal.lastUpdated)}
                        </AdminTableMeta>
                      </div>

                      <div
                        className="flex items-center justify-center relative"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <div
                          className="relative inline-flex"
                          ref={(el) => {
                            rowActionsRefs.current[rowKey] = el;
                          }}
                        >
                          <AdminRowActionsButton
                            onClick={(e) => {
                              e.stopPropagation();
                              toggleRowActions(rowKey);
                            }}
                            title="Actions"
                            aria-label="Row actions"
                          >
                            <AdminRowMenuIcon />
                          </AdminRowActionsButton>
                          {rowActionsOpen[rowKey] && rowActionsMenuLayout && (
                            <AdminFixedRowActionsMenu
                              layout={rowActionsMenuLayout}
                              menuRef={rowActionsMenuRef}
                              placementReady={rowActionsMenuPlacementReady}
                            >
                              <AdminRowMenuItem
                                onClick={(e) => {
                                  e.stopPropagation();
                                  openAppeal(appeal);
                                  setRowActionsOpen({});
                                }}
                              >
                                View
                              </AdminRowMenuItem>
                            </AdminFixedRowActionsMenu>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </DashboardMainContainer>

        <ConfigurationListPaginationFooter
          show={!loading && !error && showPagination}
          rangeStart={rangeStart}
          rangeEnd={rangeEnd}
          totalCount={totalFilteredCount}
          currentPage={effectivePage}
          totalPages={totalPages}
          onPrev={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
          onNext={() => setCurrentPage((prev) => Math.min(totalPages, prev + 1))}
          goToPage={setCurrentPage}
        />
      </div>
    </AdminLayout>
  );
};

export default Appeals;
